import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { getCompany, getUserCountryCode } from 'helper/user';
import { fetchAddress } from 'helper/address-search';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import {
  getShopifyDeliveriesQuotes,
  getShopifyOrders,
  getMSDOptimizedRoute,
} from 'actions/shopify-endpoints';
import {
  SCRIBE_SHOPIFY_STATES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import { CURRENCY_CODE } from 'constants/currency-constants';
import { COUNTRY_MAP } from 'constants/booking-constants';
import { toRouteOptimisationRequest } from 'helper/order';
import { getAlternativeFare, getOptimizedSteps } from 'helper/quoteV2-helper';
import { reorderByIndex } from 'helper/reorder';
import { FIELD_NAME } from 'views/v2/home/constants';
import AddressPickerModal from '../common/address-picker-modal';
import ActionRow from './action-row';
import TableView from './table-view';
import MapView from './map-view';
import {
  VERIFY_ADDRESS_STATUS,
  ORDER_TABLE_PAGE_SIZE,
  MAX_SINGLE_BOOKINGS_COUNT,
  FINANCIAL_STATUS,
} from './consts';
import {
  makeMap,
  getAddress,
  processError,
  convertPOIResultToAddressEntity,
  filterMSDServiceTypesByStops,
  filterMSDServiceTypesByWeight,
  findBestMatchResult,
  generateShopifyMSDQuoteRequest,
  generateShopifySingleBookingQuotesRequest,
  getFulfillmentOrdersFromOrders,
  maxMSDStopsAmongAllServiceTypes,
  maxWeightAmongAllServiceTypes,
  multipleAssignedLocationIDs,
  getCheapestServiceTypeWithQuote,
  checkPickUpAddresses,
  toBookMultiple,
  toBookMSD,
  getUniqueOrderIDs,
  hasCustomerPhone,
} from './utils';
import styles from './styles.less';
import Search from './search';
import OrderActions from './order-actions';
import ShopifyTrackEventContext from '../track-event';

const getFulfillmentOrdersToVerify = (fulfillmentOrders, addressEntityMap) => {
  if (isEmpty(fulfillmentOrders)) return [];
  return fulfillmentOrders
    .filter(x => !x.disabled)
    .filter(x => addressEntityMap[x.ID] === undefined);
};

const makeNewAddressEntityMap = addressEntityArray => {
  const addressEntityMap = addressEntityArray.reduce(
    (acc, [fulfillmentOrderID, addressEntity]) => ({
      ...acc,
      [fulfillmentOrderID]: addressEntity
        ? { status: VERIFY_ADDRESS_STATUS.unverified, ...addressEntity }
        : { status: VERIFY_ADDRESS_STATUS.failed },
    }),
    {},
  );
  return addressEntityMap;
};

const ValidationMsgLocaleKey = {
  FAIL_TO_GET_QUOTES: 'shopify.orders.validation.failToGetQuotes',
};

class ShopifyOrders extends React.Component {
  static contextType = ShopifyTrackEventContext;

  constructor(props) {
    super(props);
    this.state = {
      filter: {
        page: 1,
      },
      metadata: null,
      ordersLoading: true,
      fulfillmentOrders: null,
      selectedFulfillmentOrders: [],
      selectedLocationID: null,
      addressEntityMap: {},
      editFulfillmentOrderAddressDetails: null,
      MSDValidation: {},
      singleBookingsValidation: {},
      isRouteOptimized: false,
    };
  }

  componentDidMount() {
    this.getShopifyOrders();
    this.selectDefaultLocation();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.syncBookAgain();

    if (
      !(prevState.metadata && prevProps.shop) &&
      this.props.shop &&
      this.state.metadata
    ) {
      this.onShopifyOrderPageLoad();
    }

    if (
      prevState.selectedFulfillmentOrders.length !==
        this.state.selectedFulfillmentOrders.length ||
      prevState.addressEntityMap !== this.state.addressEntityMap
    ) {
      this.MSDBookingValidate();
      this.singleBookingsValidate();
    }
  }

  onShopifyOrderPageLoad = () => {
    const { shop, shops } = this.props;
    const { metadata } = this.state;

    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.DEFAULT,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_STORES]: shops.length,
        [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID_ALL]: shops.map(
          ({ shop }) => shop,
        ),
        [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: get(
          shop,
          'pickUpAddresses',
          [],
        ).map(adr => adr.poiID),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_PICKUP_ADDRESS]: get(
          shop,
          'pickUpAddresses',
          [],
        ).length,
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_ORDERS_ONGOING]: get(
          metadata,
          'totalOrders',
          0,
        ),
        [SCRIBE_SHOPIFY_PARAMS.IS_SHOPIFY_STORE_LINKED]: shops.length > 0,
      },
    });
  };

  onTimeRangeChange = timeRange => {
    const { filter } = this.state;
    let startDateStr, endDateStr;
    if (!timeRange || timeRange.length === 0) {
      const { startTimestamp, endTimestamp } = filter;
      if (startTimestamp > 0 || endTimestamp > 0) {
        const newState = {
          filter: {
            ...this.getFilter(),
            startTimestamp: undefined,
            endTimestamp: undefined,
            page: 1,
          },
        };
        this.setState(newState);
        this.getShopifyOrders({ filter: newState.filter });
      }
    } else if (timeRange.length === 2) {
      const [startMoment, endMoment] = timeRange;
      startDateStr = startMoment.format('YYYY-MM-DD');
      endDateStr = endMoment.format('YYYY-MM-DD');
      const newState = {
        filter: {
          ...this.getFilter(),
          startTimestamp: Math.floor(
            startMoment.startOf('day').valueOf() / 1000,
          ),
          endTimestamp: Math.floor(endMoment.endOf('day').valueOf() / 1000),
          page: 1,
        },
      };
      this.setState(newState);
      this.getShopifyOrders({ filter: newState.filter });
    }

    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_DATE,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.START_DATE]: startDateStr,
        [SCRIBE_SHOPIFY_PARAMS.END_DATE]: endDateStr,
      },
    });
  };

  onSelectFulfillmentOrder = selectedFulfillmentOrderIDs => {
    const fulfillmentOrders = this.getMergedFulfillmentOrders();
    const fulfillmentOrdersMap = makeMap(fulfillmentOrders);
    const newSelectedFulfillmentOrders = [];
    selectedFulfillmentOrderIDs.forEach(ID => {
      const fulfillmentOrder = fulfillmentOrdersMap[ID];
      if (fulfillmentOrder && !fulfillmentOrder.disabled) {
        newSelectedFulfillmentOrders.push(fulfillmentOrder);
      }
    });
    this.setState({ selectedFulfillmentOrders: newSelectedFulfillmentOrders });
  };

  onSearchChange = searchInput => {
    const keyword =
      searchInput && searchInput.length > 1 ? searchInput : undefined;

    if (keyword) {
      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.SEARCH_ORDERS,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.SEARCH_TEXT]: keyword,
        },
      });
    }

    const newState = {
      filter: {
        ...this.state.filter,
        keyword,
        page: 1,
      },
    };

    this.setState(newState);
    this.getShopifyOrders({ filter: newState.filter });
  };

  onShippingTypeChange = shippingType => {
    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHIPPING_TYPE,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.SHIPPING_TYPE_FILTER]: shippingType,
      },
    });

    const newState = {
      filter: {
        ...this.getFilter(),
        shippingType,
        page: 1,
      },
    };

    this.setState(newState);
    this.getShopifyOrders({ filter: newState.filter });
  };

  onSelectedLocationChange = selectedLocationID => {
    this.setState({
      selectedLocationID,
      selectedFulfillmentOrders: [],
    });
  };

  onPageChange = newPage => {
    const newState = {
      filter: {
        ...this.getFilter(),
        page: newPage,
      },
    };

    this.setState(newState);
    this.getShopifyOrders({ filter: newState.filter });
  };

  onEditFulfillmentOrderAddressConfirm = newAddressEntity => {
    const { addressEntityMap, editFulfillmentOrderAddressDetails } = this.state;
    const { fulfillmentOrder: editingFulfillmentOrder } =
      editFulfillmentOrderAddressDetails || {};
    this.setState({
      addressEntityMap: {
        ...addressEntityMap,
        [editingFulfillmentOrder.ID]: {
          ...addressEntityMap[editingFulfillmentOrder.ID],
          ...newAddressEntity,
          status: VERIFY_ADDRESS_STATUS.verified,
        },
      },
      editFulfillmentOrderAddressDetails: null,
    });
  };

  onEditFulfillmentOrderAddressCancel = () => {
    this.setState({
      editFulfillmentOrderAddressDetails: null,
    });
  };

  getFilter = () => {
    const { filter } = this.state;
    return {
      ...filter,
      limit: ORDER_TABLE_PAGE_SIZE,
    };
  };

  getShopifyOrders = params => {
    const { shop, userInfo } = this.props;
    const { filter, forceRefresh = false } = params || {};

    this.setState({
      ordersLoading: true,
    });
    if (shop !== null) {
      const company = getCompany(userInfo);
      getShopifyOrders(
        {
          shop: shop.shop,
          countryCode: getUserCountryCode(userInfo),
          filter: filter || this.getFilter(),
          forceRefresh,
        },
        company && company.id,
      )
        .then(({ metadata = null, orders = [] }) => {
          const fulfillmentOrders = getFulfillmentOrdersFromOrders(orders);
          this.fetchMissingAddressesForFufillmentOrders(fulfillmentOrders);
          this.setState({
            metadata,
            fulfillmentOrders,
            ordersLoading: false,
          });
        })
        .catch(err => {
          // eslint-disable-next-line
          console.log(err);
        });
    }
  };

  getMergedFulfillmentOrders = () => {
    const { fulfillmentOrders, selectedFulfillmentOrders } = this.state;
    if (!fulfillmentOrders) {
      return null;
    }
    if (!selectedFulfillmentOrders) {
      return fulfillmentOrders;
    }
    const fulfillmentOrdersMap = makeMap(fulfillmentOrders);
    const deduplicatedSelectedFulfillmentOrders = [];
    selectedFulfillmentOrders.forEach(forder => {
      if (!fulfillmentOrdersMap[forder.ID]) {
        deduplicatedSelectedFulfillmentOrders.push(forder);
      }
    });

    return this.getFilteredFulfillmentOrders([
      ...deduplicatedSelectedFulfillmentOrders,
      ...fulfillmentOrders,
    ]);
  };

  getFilteredFulfillmentOrders = fulfillmentOrders => {
    const { userInfo } = this.props;
    const selectedCity = get(userInfo, 'selectedCity');
    const city = get(selectedCity, 'city');
    const countryCode = get(selectedCity, 'countryCode');
    const expectedCurrencyCode = CURRENCY_CODE[COUNTRY_MAP[countryCode]];
    if (city && expectedCurrencyCode) {
      const fulfillmentOrdersInCity = [];
      const fulfillmentOrdersNotInCity = [];
      const fulfillmentOrderWithoutPhoneNo = [];
      const fulfillmentOrderWithWrongCurrency = [];
      fulfillmentOrders.forEach(fulfillmentOrder => {
        if (!hasCustomerPhone(fulfillmentOrder)) {
          fulfillmentOrderWithoutPhoneNo.push({
            ...fulfillmentOrder,
            disabled: true,
            reason: 'noPhoneNumber',
          });
          return;
        }
        if (
          fulfillmentOrder.currency !== expectedCurrencyCode &&
          fulfillmentOrder.financialStatus !== FINANCIAL_STATUS.PAID
        ) {
          fulfillmentOrderWithWrongCurrency.push({
            ...fulfillmentOrder,
            disabled: true,
            reason: 'wrongCurrency',
          });
          return;
        }
        fulfillmentOrdersInCity.push(fulfillmentOrder);
      });
      return [
        ...fulfillmentOrdersInCity,
        ...fulfillmentOrderWithoutPhoneNo,
        ...fulfillmentOrdersNotInCity,
        ...fulfillmentOrderWithWrongCurrency,
      ];
    }
    return [];
  };

  doForceRefresh = () => {
    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.REFRESH_ORDERS,
    });
    this.getShopifyOrders({ forceRefresh: true });
  };

  editFulfillmentOrderAddress = editFulfillmentOrderAddressDetails => {
    this.setState({
      editFulfillmentOrderAddressDetails,
    });
  };

  updateAddressEntityMap = newAddressEntityMap => {
    const { addressEntityMap } = this.state;
    this.setState({
      addressEntityMap: {
        ...addressEntityMap,
        ...newAddressEntityMap,
      },
    });
  };

  fetchAddressForFulfillmentOrder = fulfillmentOrder => {
    if (!fulfillmentOrder) {
      return null;
    }

    const { userInfo } = this.props;
    const { shippingAddress = {} } = fulfillmentOrder;
    const company = getCompany(userInfo);
    return keyword =>
      fetchAddress(keyword, null, company, {
        lat: shippingAddress.latitude,
        lng: shippingAddress.longitude,
        countryCode: shippingAddress.countryCode,
        isToggle: true,
      });
  };

  // NOTE: not in use
  // use this only for non-force refresh (i.e. filter change)
  // fetchMissingAddresses = () => {
  //   const fulfillmentOrders = this.getMergedFulfillmentOrders();
  //   const { addressEntityMap } = this.state;

  //   // only fulfillmentOrders not existing in addressEntityMap
  //   const fulfillmentOrdersToVerifyAddress = getFulfillmentOrdersToVerify(
  //     fulfillmentOrders,
  //     addressEntityMap,
  //   );
  //   this.fetchMissingAddressesForFufillmentOrders(
  //     fulfillmentOrdersToVerifyAddress,
  //   );
  // };

  fetchMissingAddressesForFufillmentOrders = fulfillmentOrders => {
    const { userInfo } = this.props;
    const { addressEntityMap } = this.state;

    // only fulfillmentOrders not existing in addressEntityMap
    const fulfillmentOrdersToVerifyAddress = getFulfillmentOrdersToVerify(
      fulfillmentOrders,
      addressEntityMap,
    );

    if (isEmpty(fulfillmentOrdersToVerifyAddress)) {
      return;
    }

    // set all to loading
    this.updateAddressEntityMap(
      fulfillmentOrdersToVerifyAddress.reduce(
        (acc, fulfillmentOrder) => ({
          ...acc,
          [fulfillmentOrder.ID]: { status: VERIFY_ADDRESS_STATUS.loading },
        }),
        {},
      ),
    );

    // fetch addresses
    const company = getCompany(userInfo);
    Promise.all(
      fulfillmentOrdersToVerifyAddress.map(fulfillmentOrder => {
        const { shippingAddress = {} } = fulfillmentOrder;
        return fetchAddress(
          getAddress(fulfillmentOrder, { withCity: true, withZip: true }),
          null,
          company,
          {
            lat: shippingAddress.latitude,
            lng: shippingAddress.longitude,
            countryCode: shippingAddress.countryCode,
            isToggle: true,
          },
        )
          .then(({ data }) => {
            const results = get(data, ['resp', 'result']);
            const bestResult = findBestMatchResult(
              results,
              shippingAddress.latitude,
              shippingAddress.longitude,
              getAddress(fulfillmentOrder),
            );
            const addressEntity = convertPOIResultToAddressEntity(
              fulfillmentOrder.shippingAddress,
              bestResult,
            );
            return [fulfillmentOrder.ID, addressEntity];
          })
          .catch(() => [fulfillmentOrder.ID, null]);
      }),
    ).then(addressEntityArray => {
      this.updateAddressEntityMap(makeNewAddressEntityMap(addressEntityArray));
    });
  };

  syncBookAgain = () => {
    const { addressEntityMap } = this.state;
    const fulfillmentOrders = this.getMergedFulfillmentOrders();
    const { bookAgainData, clearBookAgainData } = this.props;
    if (
      isEmpty(bookAgainData) ||
      isEmpty(fulfillmentOrders) ||
      isEmpty(addressEntityMap) ||
      find(addressEntityMap, v => v.status === VERIFY_ADDRESS_STATUS.loading)
    ) {
      return;
    }

    const fulfillmentOrdersToSelect = [];
    const addressEntityToUpdate = {};

    bookAgainData.map(data => {
      const order = fulfillmentOrders.find(o => o.orderID === data.orderID);
      if (!order) return;
      fulfillmentOrdersToSelect.push(order);
      addressEntityToUpdate[order.ID] = {
        ...convertPOIResultToAddressEntity(order.shippingAddress, data.address),
        status: VERIFY_ADDRESS_STATUS.verified,
      };
    });

    this.setState({
      selectedFulfillmentOrders: fulfillmentOrdersToSelect,
      addressEntityMap: {
        ...addressEntityMap,
        ...addressEntityToUpdate,
      },
    });

    clearBookAgainData();
  };

  selectDefaultLocation = () => {
    const { selectedLocationID } = this.state;
    const { shop } = this.props;

    if (
      selectedLocationID === null &&
      shop &&
      shop.pickUpAddresses &&
      shop.pickUpAddresses.length > 0
    ) {
      this.setState({
        selectedLocationID: shop.pickUpAddresses[0].shopifyLocationID,
      });
    }
  };

  setMSDValidationResult = (errorString, serviceTypesWithQuote = null) => {
    const { selectedFulfillmentOrders } = this.state;
    if (errorString !== null && selectedFulfillmentOrders.length > 0) {
      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.MULTISTOP_DELIVERY_ERROR,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.MULTISTOP_UNAVAILABLE_ERROR_MESSAGE]: errorString,
        },
      });
    }
    this.setState({
      MSDValidation: {
        error: errorString,
        serviceTypesWithQuote,
        validating: false,
      },
    });
  };

  MSDBookingValidate = async () => {
    const { selectedFulfillmentOrders, addressEntityMap } = this.state;
    const { shop, userInfo, serviceTypesInCity } = this.props;
    const company = getCompany(userInfo);
    this.setState({
      MSDValidation: {
        validating: true,
      },
    });

    const checkPickUpAddressesError = checkPickUpAddresses(
      shop,
      selectedFulfillmentOrders,
    );
    if (checkPickUpAddressesError) {
      this.setMSDValidationResult(checkPickUpAddressesError);
      return;
    }

    if (!selectedFulfillmentOrders || selectedFulfillmentOrders.length <= 1) {
      this.setMSDValidationResult(
        I18n.t('shopify.orders.validation.MSDTooLittleOrders'),
      );
      return;
    }

    if (multipleAssignedLocationIDs(selectedFulfillmentOrders)) {
      this.setMSDValidationResult(
        I18n.t('shopify.orders.validation.differentPickupAddress'),
      );
      return;
    }

    const MSDServiceTypesFilteredByStops = filterMSDServiceTypesByStops(
      serviceTypesInCity,
      selectedFulfillmentOrders.length,
    );
    if (
      !MSDServiceTypesFilteredByStops ||
      MSDServiceTypesFilteredByStops.length < 1
    ) {
      const maxMSDStops = maxMSDStopsAmongAllServiceTypes(serviceTypesInCity);
      this.setMSDValidationResult(
        I18n.t('shopify.orders.validation.noAvailableTaxiTypesForMSD', {
          maxMSDStops,
        }),
      );
      return;
    }

    const {
      totalWeight,
      serviceTypes: MSDServiceTypesFilteredByWeight,
    } = filterMSDServiceTypesByWeight(
      MSDServiceTypesFilteredByStops,
      selectedFulfillmentOrders,
    );
    if (
      !MSDServiceTypesFilteredByWeight ||
      MSDServiceTypesFilteredByWeight.length < 1
    ) {
      const maxWeight = maxWeightAmongAllServiceTypes(
        MSDServiceTypesFilteredByStops,
      );
      this.setMSDValidationResult(
        I18n.t('shopify.orders.validation.itemsAreTooHeavyForMSD', {
          maxWeight,
          totalWeight,
        }),
      );
      return;
    }

    let reorderedOrders;
    try {
      this.setState({ isRouteOptimized: false });
      const pickUpAddress = shop.pickUpAddresses.find(
        adr =>
          adr.shopifyLocationID ===
          selectedFulfillmentOrders[0].assignedLocationID,
      );
      const optimisedResult = await getMSDOptimizedRoute(
        toRouteOptimisationRequest(
          {
            recipientLocations: selectedFulfillmentOrders.map(({ ID }) => {
              const addressEntity = addressEntityMap[ID];
              return {
                latlng: addressEntity.latlng,
                id: addressEntity.poiID,
                address: {
                  name: addressEntity.keywords,
                  country_id: addressEntity.countryID,
                  cityId: addressEntity.cityID,
                  combined_address: addressEntity.address,
                },
              };
            }),
            senderLocation: {
              id: pickUpAddress.grabPoi.poiID,
              latlng: pickUpAddress.grabPoi.coordinates,
              address: {
                name: pickUpAddress.grabPoi.keywords,
                combined_address: pickUpAddress.grabPoi.address,
              },
            },
            [FIELD_NAME.SERVICE_TYPE]: {
              value: MSDServiceTypesFilteredByWeight[0].ID,
            },
          },
          userInfo,
        ),
      );

      const err = get(optimisedResult, 'error');
      if (err) {
        throw new Error(err);
      }

      const quoteV2 = get(optimisedResult, 'data.quote');
      if (!quoteV2) {
        throw new Error();
      }

      const paxExpressError = get(
        optimisedResult,
        'response.data.paxExpressError',
      );
      if (paxExpressError) {
        throw new Error(get(paxExpressError, 'message'));
      }

      const alternativeFare = getAlternativeFare(quoteV2);
      const optimizedSteps = getOptimizedSteps(alternativeFare);
      if (
        alternativeFare &&
        optimizedSteps.length !== 1 + selectedFulfillmentOrders.length
      ) {
        throw new Error();
      }
      if (alternativeFare) {
        reorderedOrders = reorderByIndex(
          [undefined].concat(selectedFulfillmentOrders),
          optimizedSteps,
        );
        reorderedOrders.shift();
        this.setState({
          selectedFulfillmentOrders: reorderedOrders,
          isRouteOptimized: true,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      if (err) console.error(err);
    }

    getShopifyDeliveriesQuotes(
      generateShopifyMSDQuoteRequest(
        shop,
        reorderedOrders || selectedFulfillmentOrders,
        MSDServiceTypesFilteredByWeight,
        addressEntityMap,
      ),
      company && company.id,
    )
      .then(({ deliveries = [] }) => {
        if (!deliveries || deliveries.length < 1) {
          throw new Error(I18n.t(ValidationMsgLocaleKey.FAIL_TO_GET_QUOTES));
        }
        const { quotes = [] } = deliveries[0];
        const errors = [];
        const serviceTypesWithQuote = [];
        quotes.forEach(quote => {
          if (quote.errors && quote.errors.length > 0) {
            errors.push(...quote.errors.map(err => err.message));
            return;
          }
          const serviceType = MSDServiceTypesFilteredByWeight.find(
            serviceType => quote.serviceID === serviceType.ID,
          );
          if (!serviceType) {
            return;
          }
          serviceTypesWithQuote.push({
            serviceType,
            quote,
          });
        });

        if (serviceTypesWithQuote.length < 1) {
          throw new Error(I18n.t(ValidationMsgLocaleKey.FAIL_TO_GET_QUOTES));
        }
        this.setMSDValidationResult(null, serviceTypesWithQuote);
      })
      .catch(
        processError(errorString => {
          this.setMSDValidationResult(errorString);
        }),
      );
  };

  getSingleBookingsBestTotalQuote = () => {
    const { singleBookingsValidation } = this.state;
    return Object.entries(
      singleBookingsValidation.serviceTypesWithQuoteMap,
    ).reduce((prev, [_, serviceTypesWithQuoteMap]) => {
      prev += getCheapestServiceTypeWithQuote(serviceTypesWithQuoteMap).quote
        .amount;
      return prev;
    }, 0);
  };

  bookMSD = () => {
    const {
      metadata,
      MSDValidation,
      selectedFulfillmentOrders,
      addressEntityMap,
      isRouteOptimized,
    } = this.state;
    const { shop, userInfo } = this.props;
    const cheapestServiceTypeWithQuote = getCheapestServiceTypeWithQuote(
      MSDValidation.serviceTypesWithQuote,
    );

    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.BOOK_MULTISTOP_DELIVERY,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_BOOKED_ORDERS]:
          selectedFulfillmentOrders.length,
        [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: getUniqueOrderIDs(
          selectedFulfillmentOrders,
        ),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_ORDERS_ONGOING]: get(
          metadata,
          'totalOrders',
          0,
        ),
        [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]: true,
      },
    });

    this.props.onBookMSD(
      toBookMSD({
        selectedFulfillmentOrders: selectedFulfillmentOrders.map(o => ({
          ...o,
          addressEntity: addressEntityMap[o.ID],
        })),
        cheapestServiceTypeWithQuote,
        serviceTypesWithQuote: MSDValidation.serviceTypesWithQuote,
        shop,
        userInfo,
        singleBookingsTotalQuote: this.getSingleBookingsBestTotalQuote(),
        isRouteOptimized,
      }),
    );
  };

  setSingleBookingsValidationResult = (
    errorString,
    serviceTypesWithQuoteMap = null,
  ) => {
    this.setState({
      singleBookingsValidation: {
        error: errorString,
        serviceTypesWithQuoteMap,
        validating: false,
      },
    });
  };

  singleBookingsValidate = () => {
    const { selectedFulfillmentOrders, addressEntityMap } = this.state;
    const { shop, userInfo, serviceTypesInCity } = this.props;
    const company = getCompany(userInfo);
    this.setState({
      singleBookingsValidation: {
        validating: true,
      },
    });

    const checkPickUpAddressesError = checkPickUpAddresses(
      shop,
      selectedFulfillmentOrders,
    );
    if (checkPickUpAddressesError) {
      this.setSingleBookingsValidationResult(checkPickUpAddressesError);
      return;
    }
    if (!selectedFulfillmentOrders || selectedFulfillmentOrders.length < 1) {
      this.setSingleBookingsValidationResult(
        I18n.t('shopify.orders.validation.singleBookingTooLittleOrders'),
      );
      return;
    }

    if (
      !selectedFulfillmentOrders ||
      selectedFulfillmentOrders.length > MAX_SINGLE_BOOKINGS_COUNT
    ) {
      this.setSingleBookingsValidationResult(
        I18n.t('shopify.orders.validation.singleBookingTooManyOrders', {
          maxOrders: MAX_SINGLE_BOOKINGS_COUNT,
        }),
      );
      return;
    }

    getShopifyDeliveriesQuotes(
      generateShopifySingleBookingQuotesRequest(
        shop,
        selectedFulfillmentOrders,
        serviceTypesInCity,
        addressEntityMap,
      ),
      company && company.id,
    )
      .then(({ deliveries = [] }) => {
        if (!deliveries || deliveries.length < 1) {
          throw new Error(I18n.t(ValidationMsgLocaleKey.FAIL_TO_GET_QUOTES));
        }
        const serviceTypesWithQuoteMap = {};
        deliveries.forEach(({ quotes }, index) => {
          const fulfillmentOrder = selectedFulfillmentOrders[index];
          const serviceTypesWithQuote = [];
          const errors = [];

          quotes.forEach(quote => {
            if (quote.errors && quote.errors.length > 0) {
              errors.push(...quote.errors.map(err => err.message));
              return;
            }
            const serviceType = serviceTypesInCity.find(
              serviceType => quote.serviceID === serviceType.ID,
            );
            if (!serviceType) {
              return;
            }
            serviceTypesWithQuote.push({
              serviceType,
              quote,
            });
          });
          if (serviceTypesWithQuote.length > 0) {
            serviceTypesWithQuoteMap[
              fulfillmentOrder.ID
            ] = serviceTypesWithQuote;
          }
        });

        if (Object.keys(serviceTypesWithQuoteMap).length < 1) {
          throw new Error(I18n.t(ValidationMsgLocaleKey.FAIL_TO_GET_QUOTES));
        }

        this.setSingleBookingsValidationResult(null, serviceTypesWithQuoteMap);
      })
      .catch(
        processError(errorString => {
          this.setSingleBookingsValidationResult(errorString);
        }),
      );
  };

  bookSingle = () => {
    const {
      metadata,
      MSDValidation,
      singleBookingsValidation,
      selectedFulfillmentOrders,
      addressEntityMap,
    } = this.state;
    const { shop, userInfo } = this.props;
    const cheapestServiceTypeWithQuote = Object.entries(
      singleBookingsValidation.serviceTypesWithQuoteMap,
    ).reduce(
      (prev, [fulfillmentOrderID, serviceTypesWithQuoteMap]) => ({
        ...prev,
        [fulfillmentOrderID]: getCheapestServiceTypeWithQuote(
          serviceTypesWithQuoteMap,
        ),
      }),
      {},
    );
    const msdCheapestServiceTypeWithQuote = MSDValidation.serviceTypesWithQuote
      ? getCheapestServiceTypeWithQuote(MSDValidation.serviceTypesWithQuote)
      : null;

    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.BOOK_NORMAL_DELIVERY,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_BOOKED_ORDERS]:
          selectedFulfillmentOrders.length,
        [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: getUniqueOrderIDs(
          selectedFulfillmentOrders,
        ),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_ORDERS_ONGOING]: get(
          metadata,
          'totalOrders',
          0,
        ),
        [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]: false,
        [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING_AVAILABLE]: !get(
          MSDValidation,
          'error',
        ),
        [SCRIBE_SHOPIFY_PARAMS.MULTISTOP_UNAVAILABLE_ERROR_MESSAGE]: get(
          MSDValidation,
          'error',
        ),
      },
    });

    this.props.onBookMultiple(
      toBookMultiple({
        selectedFulfillmentOrders: selectedFulfillmentOrders.map(o => ({
          ...o,
          addressEntity: addressEntityMap[o.ID],
        })),
        cheapestServiceTypeWithQuote,
        serviceTypesWithQuoteMap:
          singleBookingsValidation.serviceTypesWithQuoteMap,
        shop,
        userInfo,
        ...(MSDValidation &&
          !MSDValidation.error && {
            msdInfo: {
              selectedFulfillmentOrders: selectedFulfillmentOrders.map(o => ({
                ...o,
                addressEntity: addressEntityMap[o.ID],
              })),
              cheapestServiceTypeWithQuote: msdCheapestServiceTypeWithQuote,
              serviceTypesWithQuote: MSDValidation.serviceTypesWithQuote,
              shop,
              userInfo,
              singleBookingsTotalQuote: this.getSingleBookingsBestTotalQuote(),
            },
          }),
      }),
    );
  };

  render() {
    const fulfillmentOrders = this.getMergedFulfillmentOrders();
    const {
      selectedFulfillmentOrders,
      filter,
      metadata,
      ordersLoading,
      selectedLocationID,
      addressEntityMap,
      editFulfillmentOrderAddressDetails,
      MSDValidation,
      singleBookingsValidation,
    } = this.state;
    const { shop, userInfo } = this.props;
    if (shop === null || fulfillmentOrders === null) {
      return null;
    }
    const selectedCity = get(userInfo, 'selectedCity');
    const {
      fulfillmentOrder: editingFulfillmentOrder,
      addressEntity: editingAddressEntity,
    } =
      editFulfillmentOrderAddressDetails || {};

    return (
      <div className={styles.ordersMode}>
        <Search onSearchChange={this.onSearchChange} />
        <div className={styles.ordersPanel}>
          <div className={styles.orderCount}>
            Orders ({get(metadata, 'totalOrders', 0)})
          </div>
          <MapView
            shop={shop}
            ordersLoading={ordersLoading}
            addressEntityMap={addressEntityMap}
            fulfillmentOrders={fulfillmentOrders}
            selectedFulfillmentOrders={selectedFulfillmentOrders}
            onSelectFulfillmentOrder={this.onSelectFulfillmentOrder}
          />
          <ActionRow
            shop={shop}
            metadata={metadata}
            selectedLocationID={selectedLocationID}
            doForceRefresh={this.doForceRefresh}
            onTimeRangeChange={this.onTimeRangeChange}
            onShippingTypeChange={this.onShippingTypeChange}
            onSelectedLocationChange={this.onSelectedLocationChange}
          />
          <TableView
            shop={shop}
            filter={filter}
            metadata={metadata}
            selectedCity={selectedCity}
            onPageChange={this.onPageChange}
            ordersLoading={ordersLoading}
            addressEntityMap={addressEntityMap}
            fulfillmentOrders={fulfillmentOrders}
            selectedFulfillmentOrders={selectedFulfillmentOrders}
            onSelectFulfillmentOrder={this.onSelectFulfillmentOrder}
            editingFulfillmentOrder={editingFulfillmentOrder}
            editFulfillmentOrderAddress={this.editFulfillmentOrderAddress}
          />
          <AddressPickerModal
            key={
              editFulfillmentOrderAddressDetails &&
              editFulfillmentOrderAddressDetails.fulfillmentOrder.ID
            }
            visible={editFulfillmentOrderAddressDetails !== null}
            title={I18n.t('shopify.orders.addressPickerModal.editAddress')}
            subtitle={`${I18n.t(
              'shopify.orders.addressPickerModal.originalAddress',
            )}: ${getAddress(editingFulfillmentOrder)}`}
            presetAddressEntity={editingAddressEntity}
            fetchAddressFunc={this.fetchAddressForFulfillmentOrder(
              editingFulfillmentOrder,
            )}
            onCancel={this.onEditFulfillmentOrderAddressCancel}
            onPickAddress={this.onEditFulfillmentOrderAddressConfirm}
            isPickupAddress={false}
          />
        </div>
        {!isEmpty(fulfillmentOrders) && (
          <OrderActions
            bookMSD={this.bookMSD}
            MSDValidation={MSDValidation}
            bookSingle={this.bookSingle}
            singleBookingsValidation={singleBookingsValidation}
            selectedFulfillmentOrders={selectedFulfillmentOrders}
          />
        )}
      </div>
    );
  }
}

ShopifyOrders.propTypes = {
  shop: PropTypes.shape({}).isRequired,
  shops: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userInfo: PropTypes.shape({}).isRequired,
  serviceTypesInCity: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onBookMultiple: PropTypes.func.isRequired,
  onBookMSD: PropTypes.func.isRequired,
  bookAgainData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  clearBookAgainData: PropTypes.func.isRequired,
};

export default ShopifyOrders;
